import React from 'react';

import { formatDurationFromMinutes } from '@modernloop/shared/datetime';
import { Menu, MenuItem, MenuItemProps } from '@mui/material';

import { BaseProps } from 'src/components/types';

import { IS_PRODUCTION } from 'src/constants';

export type MaxDuration =
  | 5
  | 10
  | 15
  | 20
  | 25
  | 30
  | 35
  | 40
  | 45
  | 50
  | 55
  | 60
  | 75
  | 90
  | 105
  | 115
  | 120
  | 135
  | 150
  | 165
  | 180
  | 195
  | 210
  | 225
  | 240
  | 300
  | 360
  | 420
  | 480
  | 540
  | 600
  | 660
  | 720;

interface Props extends BaseProps {
  id: string;
  open: boolean;
  anchorEl: React.RefObject<HTMLElement>;
  selected?: number;
  maxDuration?: MaxDuration;
  additionalOptions?: MenuItemProps[];
  onSelect: (mins: number) => void;
  onClose: () => void;
}

/** Legacy "1 day" duration is stored as id 0. */
const LEGACY_ONE_DAY_ID = 0;
const MINUTES_IN_DAY = 1440;

// Option ids in minutes (order preserved; 65/70 intentionally skipped, 0 = legacy "1 day").
// Labels are computed at render time via `getDurationLabel` so they localize against the
// active language instead of being frozen to the locale at module load.
export const DURATION_OPTION_IDS: number[] = [
  5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 75, 80, 90, 105, 115, 120, 135, 150, 165, 180, 195, 210, 225, 240, 300,
  360, 420, 480, 540, 600, 660, 720, 0,
];

/** Locale-aware label for a duration option id (minutes); byte-identical English. */
export const getDurationLabel = (minutes: number): string =>
  minutes === LEGACY_ONE_DAY_ID
    ? formatDurationFromMinutes(MINUTES_IN_DAY)
    : formatDurationFromMinutes(minutes, { abbreviate: true });

/** Build the duration option list with localized labels — call at render time. */
export const getDurationOptions = (): MenuItemProps[] =>
  DURATION_OPTION_IDS.map((id) => ({ id: String(id), value: getDurationLabel(id) }));

const DurationMenu = ({
  additionalOptions,
  dataTestId,
  id,
  open,
  anchorEl,
  selected,
  maxDuration,
  onSelect,
  onClose,
}: Props): JSX.Element => {
  const handleSelect = (option: MenuItemProps) => {
    onSelect(Number(option.id));
    onClose();
  };

  const durationOptions = getDurationOptions();
  let options =
    selected !== undefined
      ? [...(additionalOptions ?? []), ...durationOptions].map((option) => {
          if (selected === Number(option.id)) {
            return {
              ...option,
              selected: true,
            };
          }
          return option;
        })
      : [...(additionalOptions ?? []), ...durationOptions];

  options = maxDuration
    ? options.filter((option) => option.id && Number(option.id) <= maxDuration && Number(option.id) !== 0)
    : options;

  if (!IS_PRODUCTION) {
    options = [{ id: '1', value: '1 min [NOT-SHOWN-IN-PROD]' }, ...options];
  }

  return (
    <Menu
      id={id}
      open={open}
      data-testid={dataTestId}
      disableScrollLock
      anchorEl={anchorEl.current}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      onClose={onClose}
      slotProps={{
        paper: {
          style: {
            maxHeight: '400px',
            minWidth: anchorEl?.current?.clientWidth,
          },
        },
      }}
    >
      {options.map((option) => (
        <MenuItem key={option.id} value={option.id} onClick={() => handleSelect(option)} selected={option.selected}>
          {option.value}
        </MenuItem>
      ))}
    </Menu>
  );
};

export default DurationMenu;
